/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

// Helpers shared by the instance configuration picker on the create workspace page and in the change
// instance type modal.

import showdown from 'showdown';

// ------------------------------------------------------------------------------------------------
// Hyperthreading
// ------------------------------------------------------------------------------------------------

// Instance families that only accept ThreadsPerCore=1, so hyperthreading cannot be turned on for
// them. g5 is the A10G hardware, g7 (non-e) is the RTX PRO 4500 hardware. Keep this in sync with the
// family list in the change-instance-type-ec2-environment-sc workflow step.
export const NO_HYPERTHREADING_INSTANCE_FAMILIES = ['g5', 'g7'];

/**
 * The family part of an instance type, with any `/N` core count suffix removed: `g5.xlarge/4` gives
 * `g5`.
 */
export function getInstanceFamily(instanceType) {
  return String(instanceType || '')
    .split('/')[0]
    .split('.')[0];
}

/**
 * Whether the hyperthreading choice is fixed for an instance type.
 *
 * Matched on the exact family token rather than a prefix. `g7` must not also catch `g7e`, which is a
 * separate family that does accept a ThreadsPerCore override: compare `g7.48xlarge/192` against
 * `g7e.48xlarge/96`.
 *
 * On the `/N` suffix: for the locked families it is deliberately set to the thread count, which
 * equals the physical core count on hardware that runs one thread per core. So if it ever does reach
 * the template it asks for the instance's native topology and changes nothing. Do not "correct" it
 * to half the vCPU count, that only applies to families like g7e.
 */
export function isHyperthreadingLockedInstanceType(instanceType) {
  return NO_HYPERTHREADING_INSTANCE_FAMILIES.includes(getInstanceFamily(instanceType));
}

// ------------------------------------------------------------------------------------------------
// Description
// ------------------------------------------------------------------------------------------------

// The env type config `desc` is authored in markdown by admins. This deliberately uses a bare
// showdown converter rather than the base-ui Showdown wrapper: that wrapper decorates the output
// with Semantic UI classes (`ui large header`, `ui list`, ...) sized for full page content, which
// override the compact styling the picker wants inside a table.
const converter = new showdown.Converter();
// setFlavor resets any previously set options, so it has to come before the setOption calls.
converter.setFlavor('vanilla');
converter.setOption('parseImgDimensions', true);
converter.setOption('tables', true);
converter.setOption('simpleLineBreaks', true);
converter.setOption('openLinksInNewWindow', true);

/**
 * Converts admin-authored markdown to html for dangerouslySetInnerHTML, after stripping the obvious
 * script vectors. Mirrors the sanitising done for estimatedCostInfoHtml on the env type config model.
 */
export function descToHtml(desc) {
  if (!desc) return '';
  const sanitized = String(desc)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+\s*=/gi, '');
  return converter.makeHtml(sanitized);
}

// "1 CPU (2 threads) / 1 GiB Memory", "2 CPUs (4 threads) / 16 GiB Memory". The threads part and the
// trailing "Memory" are optional so that small variations in how admins write it still parse.
const SPEC_PATTERN = /(\d+)\s*v?CPUs?\b\s*(?:\(\s*(\d+)\s*threads?\s*\))?\s*\/\s*(\d+(?:\.\d+)?)\s*(GiB|GB|TiB|TB)\b(?:\s*(?:Memory|RAM))?/i;
const DCV_PATTERN = /dcv not supported/i;
// The "*DCV not supported" marker as admins write it, with the leading asterisk and an optional
// full stop. Only this form is cut out of the details, so a sentence that merely mentions DCV stays
// readable.
const DCV_MARKER_PATTERN = /\s*\*\s*DCV not supported\.?/gi;
const EDGE_PUNCTUATION = /^[\s*_\-–—|,/]+|[\s*_\-–—|,/]+$/g;

function stripEmphasis(text) {
  return text.replace(/\*\*|__/g, '');
}

function plainText(markdown) {
  return String(markdown || '')
    .replace(/<[^>]*>/g, '')
    .replace(/[*_`#>]/g, '')
    .trim();
}

/**
 * Pulls the hardware summary out of a config description so the picker can show it in columns.
 *
 * Descriptions are free text, so this only recognises the "N CPU (M threads) / X GiB Memory" form
 * the workspace card and detail page already read. When it isn't found, `hasSpecs` is false and
 * `summary` carries the description's first line for display instead.
 *
 * @param {string} desc The config description, in markdown
 * @returns {{ hasSpecs: boolean, cpus: number|null, threads: number|null, memory: string|null,
 *   dcvUnsupported: boolean, details: string, summary: string }}
 *   `details` is the rest of the description, still in markdown, with the specs and the DCV marker
 *   removed.
 */
export function parseConfigSpecs(desc) {
  const text = String(desc || '');
  const lines = text.split(/\r?\n/);
  let specs = null;
  const detailLines = [];

  lines.forEach(line => {
    if (!specs) {
      const plain = stripEmphasis(line);
      const match = plain.match(SPEC_PATTERN);
      if (match) {
        specs = {
          cpus: Number(match[1]),
          threads: match[2] ? Number(match[2]) : null,
          memory: `${match[3]} ${match[4]}`,
        };
        const rest = plain
          .replace(match[0], '')
          .replace(DCV_MARKER_PATTERN, '')
          .replace(DCV_PATTERN, '')
          .replace(EDGE_PUNCTUATION, '');
        if (rest) detailLines.push(rest);
        return;
      }
    }
    detailLines.push(line.replace(DCV_MARKER_PATTERN, ''));
  });

  const details = detailLines.join('\n').trim();
  const firstLine = lines.map(plainText).find(line => line.length > 0) || '';

  return {
    hasSpecs: !!specs,
    cpus: specs ? specs.cpus : null,
    threads: specs ? specs.threads : null,
    memory: specs ? specs.memory : null,
    dcvUnsupported: DCV_PATTERN.test(text),
    details,
    summary: specs ? '' : firstLine.replace(DCV_MARKER_PATTERN, '').trim(),
  };
}

// ------------------------------------------------------------------------------------------------
// Cost
// ------------------------------------------------------------------------------------------------

/**
 * The hourly rate in USD for a config, or null when it can't be read as a number.
 *
 * `estimatedCostInfo` is either JSON keyed by region (`{"ap-southeast-2":"0.52"}`) or legacy text
 * such as "0.05 USD/Hour (1.27 USD/Day)". For JSON the region is looked up the same way
 * getEstimatedCostForRegion does: the given region, then the primary region, then the first entry.
 *
 * @param {string} estimatedCostInfo
 * @param {string} [region]
 * @returns {number|null}
 */
export function getHourlyRate(estimatedCostInfo, region) {
  if (!estimatedCostInfo) return null;
  const raw = String(estimatedCostInfo).trim();

  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const has = key => !!key && Object.prototype.hasOwnProperty.call(parsed, key);
        const primaryRegion = typeof process !== 'undefined' && process.env && process.env.REACT_APP_AWS_REGION;
        const key = [region, primaryRegion].find(has) || Object.keys(parsed)[0];
        if (key === undefined) return null;
        const rate = parseFloat(parsed[key]);
        return Number.isFinite(rate) ? rate : null;
      }
    } catch (error) {
      // Not valid JSON, fall through to the legacy text form.
    }
  }

  const match = raw.match(/(\d+(?:\.\d+)?)\s*USD\s*\/\s*h(?:ou)?r/i);
  return match ? parseFloat(match[1]) : null;
}

export function formatHourlyRate(rate) {
  return `${rate.toFixed(2)} USD/hr`;
}

export function formatDailyRate(rate) {
  return `${(Math.round(rate * 24 * 100) / 100).toFixed(2)} USD/day`;
}

/**
 * "+0.15 USD/hr", "-0.04 USD/hr" or "Same cost". Rounded to the cent first, so two rates that
 * display the same are not reported as different.
 */
export function formatRateDifference(difference) {
  const cents = Math.round(difference * 100);
  if (cents === 0) return 'Same cost';
  const sign = cents > 0 ? '+' : '-';
  return `${sign}${(Math.abs(cents) / 100).toFixed(2)} USD/hr`;
}
